import { ChangeDetectionStrategy, Component, computed, ElementRef, Injector, Input, signal, ViewChild } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { CheckboxModule } from 'primeng/checkbox';
import { ImageCroppedEvent, ImageCropperComponent, ImageTransform } from 'ngx-image-cropper';
import { BlogService } from 'src/app/api/blog.service';
import {
  BlogBlock,
  BlogBlockType,
  BlogCtaContent,
  BlogImageContent,
  BlogPost,
  BlogRichTextContent,
  BlogSeries,
  BlogTag,
  BlogVideoContent,
} from 'src/app/api/types/blog';
import { UrlService } from 'src/app/services/url.service';
import { ErrorService } from 'src/app/services/error.service';
import { DataPadCardHeaderComponent } from 'src/app/ui/data-pad/data-pad-card-header/data-pad-card-header.component';
import { DataPadComponentBase } from 'src/app/ui/data-pad/data-pad-component-base';
import { DataPadComponents, DataPadService } from 'src/app/ui/data-pad/data-pad.service';
import { ProseMirrorComponent } from 'src/app/ui/prose-mirror/prose-mirror.component';
import { SaveButtonComponent } from 'src/app/ui/save-button/save-button.component';
import { IconService } from 'src/app/ui/icon.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { LocalAuthService } from 'src/app/auth/local-auth.service';
import { IconButtonComponent } from 'src/app/ui/icon-button/icon-button.component';
import { YoutubeEmbedComponent } from 'src/app/ui/youtube-embed/youtube-embed.component';
import { TooltipModule } from 'primeng/tooltip';
import { DialogService } from 'primeng/dynamicdialog';
import { IncidentModalService } from '../../incidents/modals/incident-modal.service';
import { MessageBoxResults } from '../../incidents/modals/message-box/message-box.component';
import { ImageService } from 'src/app/utility/image.service';
import { ConfigService } from 'src/app/config.service';

/** Matches the public post-page hero aspect ratio. */
export const BLOG_HERO_ASPECT_RATIO = 16 / 9;
const BLOG_HERO_MAX_WIDTH = 1600;
const BLOG_HERO_MAX_HEIGHT = 900;

export interface EditBlogPostDatapadInputData {
  editPost: BlogPost | null;
}

@Component({
  selector: 'app-edit-blog-post-datapad',
  imports: [
    CardModule,
    DataPadCardHeaderComponent,
    ButtonModule,
    SaveButtonComponent,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    MultiSelectModule,
    DialogModule,
    ProseMirrorComponent,
    FontAwesomeModule,
    IconButtonComponent,
    YoutubeEmbedComponent,
    CheckboxModule,
    TooltipModule,
    ImageCropperComponent,
  ],
  providers: [DialogService],
  templateUrl: './edit-blog-post-datapad.component.html',
  styleUrl: './edit-blog-post-datapad.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditBlogPostDatapadComponent extends DataPadComponentBase {
  protected override panelName: DataPadComponents = 'editBlogPost';
  protected override inputData: EditBlogPostDatapadInputData = { editPost: null };

  @Input() set editPost(value: BlogPost | null) {
    this.inputData['editPost'] = value;
  }
  get editPost(): BlogPost | null {
    return this.inputData['editPost'];
  }

  @ViewChild(SaveButtonComponent) saveButton!: SaveButtonComponent;
  @ViewChild('heroImageInput') heroImageInput?: ElementRef<HTMLInputElement>;

  readonly heroAspectRatio = BLOG_HERO_ASPECT_RATIO;

  seriesOptions = signal<BlogSeries[]>([]);
  tagOptions = signal<BlogTag[]>([]);
  blocks = signal<BlogBlock[]>([]);
  heroImageUrl = signal<string | null>(null);
  heroImageFile: File | undefined;
  showAddTagDialog = signal(false);
  showAddSeriesDialog = signal(false);

  croppingHero = signal(false);
  heroImageChangedEvent: Event | null = null;
  croppedHeroBlob: Blob | null = null;
  rotateValue = signal(0);
  imageTransform = computed<ImageTransform>(() => ({ rotate: this.rotateValue() }));
  private heroPreviewObjectUrl: string | null = null;

  constructor(
    dataPadService: DataPadService,
    injector: Injector,
    private fb: FormBuilder,
    private blogService: BlogService,
    private errorService: ErrorService,
    private messageService: MessageService,
    public icons: IconService,
    private auth: LocalAuthService,
    private urlService: UrlService,
    private modalService: IncidentModalService,
    private dialogService: DialogService,
    private router: Router,
    private imageService: ImageService,
    private config: ConfigService,
  ) {
    super(dataPadService, injector);
  }

  form = this.fb.group({
    title: this.fb.nonNullable.control('', { validators: Validators.required }),
    slug: this.fb.nonNullable.control(''),
    excerpt: this.fb.nonNullable.control(''),
    author: this.fb.nonNullable.control(this.auth.currentUser?.name ?? '', { validators: Validators.required }),
    seriesId: this.fb.control<number | null>(null),
    tagIds: this.fb.nonNullable.control<number[]>([]),
  });

  addTagForm = this.fb.group({
    name: this.fb.nonNullable.control('', { validators: Validators.required }),
    slug: this.fb.nonNullable.control(''),
  });

  addSeriesForm = this.fb.group({
    name: this.fb.nonNullable.control('', { validators: Validators.required }),
    slug: this.fb.nonNullable.control(''),
  });

  override onInit(): void {
    this.blogService.getSeries().subscribe({
      next: (series) => this.seriesOptions.set(series),
      error: (err) => this.errorService.error('Error loading blog series', err, this.messageService),
    });
    this.blogService.getTags().subscribe({
      next: (tags) => this.tagOptions.set(tags),
      error: (err) => this.errorService.error('Error loading blog tags', err, this.messageService),
    });

    const post = this.editPost;
    if (post) {
      this.form.patchValue({
        title: post.title,
        slug: post.slug,
        excerpt: post.excerpt ?? '',
        author: post.author,
        seriesId: post.series?.id ?? null,
        tagIds: post.tags.map(t => t.id),
      });
      this.blocks.set(post.blocks ?? []);
      this.heroImageUrl.set(post.heroImage);
    }
  }

  override onChanges(): void {}
  override onDestroy(): void {
    this.revokeHeroPreviewObjectUrl();
  }

  get isCreateMode(): boolean {
    return !this.editPost;
  }

  openAddTagDialog() {
    this.addTagForm.reset({ name: '', slug: '' });
    this.showAddTagDialog.set(true);
  }

  createTag() {
    if (this.addTagForm.invalid) {
      this.addTagForm.markAllAsTouched();
      this.messageService.add({ severity: 'warn', summary: 'Missing fields', detail: 'Tag name is required' });
      return;
    }

    const values = this.addTagForm.getRawValue();
    this.blogService.createTag({
      name: values.name,
      slug: values.slug || undefined,
    }).subscribe({
      next: (created) => {
        this.tagOptions.update(tags => [...tags, created].sort((a, b) => a.name.localeCompare(b.name)));
        const currentIds = this.form.controls.tagIds.value;
        if (!currentIds.includes(created.id)) {
          this.form.controls.tagIds.setValue([...currentIds, created.id]);
        }
        this.showAddTagDialog.set(false);
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Blog tag created' });
      },
      error: (err) => this.errorService.error('Error creating blog tag', err, this.messageService),
    });
  }

  manageTags() {
    this.closeDataPad();
    void this.router.navigate(['/mission-control/blog/tags']);
  }

  openAddSeriesDialog() {
    this.addSeriesForm.reset({ name: '', slug: '' });
    this.showAddSeriesDialog.set(true);
  }

  createSeries() {
    if (this.addSeriesForm.invalid) {
      this.addSeriesForm.markAllAsTouched();
      this.messageService.add({ severity: 'warn', summary: 'Missing fields', detail: 'Series name is required' });
      return;
    }

    const values = this.addSeriesForm.getRawValue();
    this.blogService.createSeries({
      name: values.name,
      slug: values.slug || undefined,
    }).subscribe({
      next: (created) => {
        this.seriesOptions.update(series => [...series, created].sort((a, b) => a.name.localeCompare(b.name)));
        this.form.controls.seriesId.setValue(created.id);
        this.showAddSeriesDialog.set(false);
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Blog series created' });
      },
      error: (err) => this.errorService.error('Error creating blog series', err, this.messageService),
    });
  }

  manageSeries() {
    this.closeDataPad();
    void this.router.navigate(['/mission-control/blog/series']);
  }

  previewOnStaging() {
    const slug = this.form.controls.slug.value.trim() || this.editPost?.slug;
    if (!slug) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Missing slug',
        detail: 'Set a slug (or leave blank to auto-generate on save) before previewing',
      });
      return;
    }
    const base = this.config.mainSiteStagingUrl.replace(/\/$/, '');
    window.open(`${base}/blog/${slug}`, '_blank', 'noopener');
  }

  addBlock(blockType: BlogBlockType) {
    const sortOrder = this.blocks().length;
    let content: BlogBlock['content'];
    switch (blockType) {
      case 'rich_text':
        content = { html: '' } satisfies BlogRichTextContent;
        break;
      case 'image':
        content = { url: '', alt: '', caption: '' } satisfies BlogImageContent;
        break;
      case 'video':
        content = { youtubeId: '' } satisfies BlogVideoContent;
        break;
      case 'cta':
        content = { label: '', url: '', openInNewTab: true } satisfies BlogCtaContent;
        break;
    }
    this.blocks.update(blocks => [...blocks, { blockType, content, sortOrder }]);
  }

  removeBlock(index: number) {
    this.blocks.update(blocks => blocks.filter((_, i) => i !== index).map((b, i) => ({ ...b, sortOrder: i })));
  }

  moveBlock(index: number, direction: -1 | 1) {
    const newIndex = index + direction;
    const current = [...this.blocks()];
    if (newIndex < 0 || newIndex >= current.length) return;
    [current[index], current[newIndex]] = [current[newIndex], current[index]];
    this.blocks.set(current.map((b, i) => ({ ...b, sortOrder: i })));
  }

  updateRichText(index: number, html: string) {
    this.blocks.update(blocks => blocks.map((b, i) =>
      i === index ? { ...b, content: { html } satisfies BlogRichTextContent } : b,
    ));
  }

  updateImageField(index: number, field: keyof BlogImageContent, value: string) {
    this.blocks.update(blocks => blocks.map((b, i) => {
      if (i !== index) return b;
      const content = b.content as BlogImageContent;
      return { ...b, content: { ...content, [field]: value } };
    }));
  }

  onImageFileSelected(index: number, event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.blogService.uploadImage(file).subscribe({
      next: ({ url }) => this.updateImageField(index, 'url', url),
      error: (err) => this.errorService.error('Error uploading image', err, this.messageService),
    });
  }

  updateVideoUrl(index: number, url: string) {
    const youtubeId = this.urlService.getYoutubeVideoId(url) ?? '';
    this.blocks.update(blocks => blocks.map((b, i) =>
      i === index ? { ...b, content: { youtubeId } satisfies BlogVideoContent } : b,
    ));
  }

  updateCtaField(index: number, field: keyof BlogCtaContent, value: string | boolean) {
    this.blocks.update(blocks => blocks.map((b, i) => {
      if (i !== index) return b;
      const content = b.content as BlogCtaContent;
      return { ...b, content: { ...content, [field]: value } };
    }));
  }

  getVideoUrl(index: number): string {
    const block = this.blocks()[index];
    const content = block?.content as BlogVideoContent;
    return content?.youtubeId ? `https://www.youtube.com/watch?v=${content.youtubeId}` : '';
  }

  onHeroImageSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.[0]) {
      return;
    }
    this.heroImageChangedEvent = event;
    this.croppedHeroBlob = null;
    this.rotateValue.set(0);
    this.croppingHero.set(true);
  }

  onHeroImageCropped(event: ImageCroppedEvent) {
    this.croppedHeroBlob = event.blob ?? null;
  }

  onRotateInput(event: Event) {
    const value = Number((event.target as HTMLInputElement).value);
    this.rotateValue.set(Number.isFinite(value) ? value : 0);
  }

  nudgeRotate(degrees: number) {
    const next = Math.max(-180, Math.min(180, this.rotateValue() + degrees));
    this.rotateValue.set(next);
  }

  async confirmHeroCrop() {
    if (!this.croppedHeroBlob) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Crop required',
        detail: 'Adjust the crop area before confirming',
      });
      return;
    }

    try {
      const resized = await this.imageService.resizeImageBlob(
        this.croppedHeroBlob,
        BLOG_HERO_MAX_WIDTH,
        BLOG_HERO_MAX_HEIGHT,
        { outputType: 'image/jpeg', quality: 0.85 },
      );
      this.heroImageFile = new File([resized], 'blog-hero.jpg', {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
      this.revokeHeroPreviewObjectUrl();
      this.heroPreviewObjectUrl = URL.createObjectURL(this.heroImageFile);
      this.heroImageUrl.set(this.heroPreviewObjectUrl);
      this.resetHeroCropper();
    } catch (err) {
      this.errorService.error('Error processing hero image', err, this.messageService);
    }
  }

  cancelHeroCrop() {
    this.resetHeroCropper();
  }

  clearHeroImage() {
    this.heroImageFile = undefined;
    this.revokeHeroPreviewObjectUrl();
    this.heroImageUrl.set(null);
    this.resetHeroCropper();
  }

  private resetHeroCropper() {
    this.croppingHero.set(false);
    this.heroImageChangedEvent = null;
    this.croppedHeroBlob = null;
    this.rotateValue.set(0);
    if (this.heroImageInput?.nativeElement) {
      this.heroImageInput.nativeElement.value = '';
    }
  }

  private revokeHeroPreviewObjectUrl() {
    if (this.heroPreviewObjectUrl) {
      URL.revokeObjectURL(this.heroPreviewObjectUrl);
      this.heroPreviewObjectUrl = null;
    }
  }

  save = () => {
    this.persistPost({
      onComplete: () => {
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Blog post saved' });
        this.saveButton?.saveComplete();
        this.closeDataPad();
      },
    });
  };

  publish() {
    const isFirstPublish = this.isCreateMode || this.editPost?.status !== 'published';
    const title = this.form.controls.title.value || this.editPost?.title || 'this post';

    this.modalService.showMessageBox(
      this.dialogService,
      isFirstPublish ? 'Publish Blog Post' : 'Update Live Blog Post',
      isFirstPublish
        ? `Publish "${title}" and email subscribers?`
        : `Refresh the live page for "${title}" without emailing subscribers?`,
      {
        confirmButtonText: isFirstPublish ? 'Publish & Email' : 'Update Live Site',
        confirmButtonSeverity: isFirstPublish ? 'success' : 'info',
      },
    ).onClose.subscribe((result) => {
      if (result !== MessageBoxResults.Confirmed) {
        return;
      }

      this.persistPost({
        onComplete: (postId) => {
          this.saveButton?.saveComplete();
          this.blogService.publishPost(postId, isFirstPublish).subscribe({
            next: (published) => {
              this.editPost = published;
              this.messageService.add({
                severity: 'success',
                summary: isFirstPublish ? 'Published' : 'Updated',
                detail: isFirstPublish
                  ? 'Blog post published and subscribers will be emailed'
                  : 'Live blog page will refresh without emailing subscribers',
              });
              this.closeDataPad();
            },
            error: (err) => this.errorService.error(
              isFirstPublish ? 'Error publishing blog post' : 'Error updating live blog post',
              err,
              this.messageService,
            ),
          });
        },
      });
    });
  }

  private persistPost(options: { onComplete: (postId: number) => void }) {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.messageService.add({ severity: 'warn', summary: 'Missing fields', detail: 'Title and author are required' });
      this.saveButton?.saveComplete();
      return;
    }

    const values = this.form.getRawValue();
    const payload = {
      title: values.title,
      slug: values.slug || undefined,
      excerpt: values.excerpt || undefined,
      author: values.author,
      seriesId: values.seriesId,
      tagIds: values.tagIds,
    };

    const saveBlocks = (postId: number) => {
      this.blogService.replaceBlocks(postId, this.blocks()).subscribe({
        next: (updated) => {
          this.editPost = updated;
          if (this.heroImageFile) {
            this.blogService.uploadHeroImage(postId, this.heroImageFile).subscribe({
              next: (hero) => {
                this.editPost = {...updated, heroImage: hero.url};
                this.heroImageFile = undefined;
                options.onComplete(postId);
              },
              error: (err) => this.errorService.error('Error uploading hero image', err, this.messageService),
            });
          } else {
            options.onComplete(postId);
          }
        },
        error: (err) => this.errorService.error('Error saving blog blocks', err, this.messageService),
      });
    };

    if (this.editPost) {
      this.blogService.updatePost(this.editPost.id, payload).subscribe({
        next: (updated) => {
          this.editPost = updated;
          saveBlocks(updated.id);
        },
        error: (err) => this.errorService.error('Error saving blog post', err, this.messageService),
      });
      return;
    }

    this.blogService.createPost(payload).subscribe({
      next: (created) => {
        this.editPost = created;
        saveBlocks(created.id);
      },
      error: (err) => this.errorService.error('Error creating blog post', err, this.messageService),
    });
  }

  asRichText(content: BlogBlock['content']): BlogRichTextContent {
    return content as BlogRichTextContent;
  }

  asImage(content: BlogBlock['content']): BlogImageContent {
    return content as BlogImageContent;
  }

  asCta(content: BlogBlock['content']): BlogCtaContent {
    return content as BlogCtaContent;
  }

  asVideo(content: BlogBlock['content']): BlogVideoContent {
    return content as BlogVideoContent;
  }
}
